import { useAccountStore } from '@/store';
import { useForbiddenPaths } from '../useForbiddenPaths/useForbiddenPaths';
import { useCallback, useEffect, useRef } from 'react';
import { accountService } from '@/services';
import { logToSentryOnFetchError } from '@/utils';
import { ExpertNetworkStatusEnum } from '@/types';
import { usePathname } from 'next/navigation';
import { config } from '@/config';

export const useFetchSummaryIfAuthorised = () => {
    const pathname = usePathname();

    const { updateSummary, expertNetworkStatus } = useAccountStore((state) => ({
        updateSummary: state.updateSummary,
        expertNetworkStatus: state.expertNetworkStatus,
    }));

    const { isAllowedToRequestAuthorizedData } = useForbiddenPaths();

    const hasStatusToCheckSummary =
        config.alwaysRequestSummary ||
        expertNetworkStatus === ExpertNetworkStatusEnum.REGISTERED ||
        expertNetworkStatus === ExpertNetworkStatusEnum.INVITED;

    const hasPerformedInitialFetch = useRef(false);
    const lastFetchedPathname = useRef<string | null>(null);

    const fetchSummary = useCallback(
        async (options?: { setPerformedInitialFetch?: boolean; setLastPathname?: boolean }) => {
            try {
                const summary = await accountService.summary();
                updateSummary(summary);

                if (options?.setPerformedInitialFetch) {
                    hasPerformedInitialFetch.current = true;
                }
                if (options?.setLastPathname) {
                    lastFetchedPathname.current = pathname;
                }
            } catch (err) {
                logToSentryOnFetchError(err);
            }
        },
        [pathname, updateSummary],
    );

    useEffect(() => {
        if (!isAllowedToRequestAuthorizedData) {
            hasPerformedInitialFetch.current = false;
            lastFetchedPathname.current = null;
            return;
        }

        if (!hasPerformedInitialFetch.current) {
            void fetchSummary({ setPerformedInitialFetch: true, setLastPathname: true });
        }
    }, [isAllowedToRequestAuthorizedData, fetchSummary]);

    useEffect(() => {
        if (
            hasPerformedInitialFetch.current &&
            pathname !== lastFetchedPathname.current &&
            isAllowedToRequestAuthorizedData &&
            hasStatusToCheckSummary
        ) {
            void fetchSummary({ setLastPathname: true });
        }
    }, [pathname, isAllowedToRequestAuthorizedData, hasStatusToCheckSummary, fetchSummary]);
};
